import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiHome,
  FiTag,
  FiMapPin,
  FiCheck,
  FiPlus,
  FiMinus,
  FiFileText,
} from "react-icons/fi";
import { BiBuildingHouse, BiBed, BiBath } from "react-icons/bi";
import { MdOutlineBalcony } from "react-icons/md";
import axios from "axios";
import { Baseurl } from "../../../config/BaseUrl";
import {
  setBasicDetails,
  setLocationDetails,
  setPropertyProfile,
  setbasicdetailScooring,
  setLocalityScooring,
  setmainpropertyScooring,
  setpropertyScooring,
  setPostPropertyStepsStatus,
} from "../../../redux/property/PropertySlice";

const DEFAULT_CITIES = [
  "Hyderabad",
  "Bangalore",
  "Mumbai",
  "Pune",
  "Chennai",
  "Delhi NCR",
  "Kolkata",
  "Ahmedabad",
];

const DEFAULT_HYD_LOCALITIES = [
  "Banjara Hills",
  "Jubilee Hills",
  "Gachibowli",
  "Hitec City",
  "Madhapur",
  "Kondapur",
  "Kukatpally",
  "Manikonda",
  "Miyapur",
  "Tellapur",
  "Nallagandla",
  "Kokapet",
  "Financial District",
  "Begumpet",
  "Secunderabad",
];

const DEFAULT_APARTMENTS = [
  "My Home Bhooja",
  "Aparna Serene Park",
  "Prestige High Fields",
  "Jayabheri The Peak",
  "Lodha Bellezza",
  "Rajapushpa Atria",
  "SMR Vinay Iconia",
  "Lanco Hills",
];

const StepOneDetails = ({ onNext }) => {
  const dispatch = useDispatch();
  const { BasicDetails, LocationDetails, PropertyProfile } = useSelector(
    (state) => state.Property
  );
  const { superCatTotal } = useSelector((state) => state.superCategory);
  const { catTotal } = useSelector((state) => state.Category);
  const { subCatTotal } = useSelector((state) => state.SubCategory);

  // Form States - Section 1: Basic Details
  const [lookingTo, setLookingTo] = useState("Residential");
  const [listingType, setListingType] = useState("Sell");
  const [propertyCategory, setPropertyCategory] = useState("Flat/Apartment");

  // Section 2: Property Location
  const [city, setCity] = useState("Hyderabad");
  const [apartment, setApartment] = useState("");
  const [locality, setLocality] = useState("");
  const [subLocality, setSubLocality] = useState("");
  const [houseNumber, setHouseNumber] = useState("");

  // Autocomplete lists
  const [citiesList, setCitiesList] = useState(DEFAULT_CITIES);
  const [localitiesList, setLocalitiesList] = useState(DEFAULT_HYD_LOCALITIES);
  const [apartmentsList, setApartmentsList] = useState(DEFAULT_APARTMENTS);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showLocalityDropdown, setShowLocalityDropdown] = useState(false);
  const [showApartmentDropdown, setShowApartmentDropdown] = useState(false);

  // Section 3: Property Profile
  const [bedrooms, setBedrooms] = useState("3");
  const [bathrooms, setBathrooms] = useState("3");
  const [balconies, setBalconies] = useState("1");
  const [otherRooms, setOtherRooms] = useState({
    poojaRoom: false,
    studyRoom: false,
    servantRoom: false,
    storeRoom: false,
  });
  const [furnishing, setFurnishing] = useState("Un-furnished");
  const [availabilityStatus, setAvailabilityStatus] = useState("Ready to move");

  // Section 4: Area Details & Parking
  const [showAreaInput, setShowAreaInput] = useState({
    plotArea: false,
    carpetArea: true,
    landArea: false,
    builtupArea: false,
    superBuiltupArea: false,
  });
  const [areas, setAreas] = useState({
    carpetArea: "1625",
    builtupArea: "",
    superBuiltupArea: "",
    plotArea: "",
    landArea: "",
  });
  const [coveredParking, setCoveredParking] = useState(1);
  const [openParking, setOpenParking] = useState(0);
  const [floorDetails, setFloorDetails] = useState("First Floor");

  // Error States
  const [errors, setErrors] = useState({});

  // Prefill existing data if available
  useEffect(() => {
    if (BasicDetails) {
      if (BasicDetails.supercatName) setLookingTo(BasicDetails.supercatName);
      if (BasicDetails.catName) setListingType(BasicDetails.catName);
      if (BasicDetails.subcatName) setPropertyCategory(BasicDetails.subcatName);
    }
    if (LocationDetails) {
      if (LocationDetails.city) setCity(LocationDetails.city);
      if (LocationDetails.apartment) setApartment(LocationDetails.apartment);
      if (LocationDetails.locality) setLocality(LocationDetails.locality);
      if (LocationDetails.subLocality) setSubLocality(LocationDetails.subLocality);
      if (LocationDetails.housenumber) setHouseNumber(LocationDetails.housenumber);
    }
    if (PropertyProfile) {
      if (PropertyProfile.noofBedrooms) setBedrooms(PropertyProfile.noofBedrooms);
      if (PropertyProfile.noofBathrooms) setBathrooms(PropertyProfile.noofBathrooms);
      if (PropertyProfile.balconies) setBalconies(PropertyProfile.balconies);
      if (PropertyProfile.furnishedStatus) setFurnishing(PropertyProfile.furnishedStatus);
      if (PropertyProfile.carpetArea) {
        setAreas((prev) => ({ ...prev, carpetArea: PropertyProfile.carpetArea }));
        setShowAreaInput((prev) => ({ ...prev, carpetArea: true }));
      }
      if (PropertyProfile.poojaroom) {
        setOtherRooms((prev) => ({ ...prev, poojaRoom: true }));
      }
    }
  }, [BasicDetails, LocationDetails, PropertyProfile]);

  // Helper to safely get string name from string or object
  const getItemName = (item, defaultKey = "name") => {
    if (!item) return "";
    if (typeof item === "string") return item;
    if (typeof item === "object") {
      return item[defaultKey] || item.name || item.apartment || item.locality || item.city || "";
    }
    return String(item);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!e.target.closest(".mgh-form-group")) {
        setShowCityDropdown(false);
        setShowApartmentDropdown(false);
        setShowLocalityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  // Fetch Cities from API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const resp = await axios.get(`${Baseurl}/api/v1/property/allcities`);
        if (resp.data && resp.data.success && Array.isArray(resp.data.cities)) {
          const validCities = resp.data.cities
            .map((c) => getItemName(c, "city"))
            .filter((c) => typeof c === "string" && c.trim() !== "");
          if (validCities.length > 0) setCitiesList(validCities);
        }
      } catch (err) {
        // fallback to default cities
      }
    };
    fetchCities();
  }, []);

  // Fetch Localities & Apartments when city changes
  useEffect(() => {
    if (!city) return;
    const fetchCityData = async () => {
      try {
        const [locResp, aptResp] = await Promise.all([
          axios.get(`${Baseurl}/api/v1/property/alllocalities/${city}`),
          axios.get(`${Baseurl}/api/v1/property/apartmentnew/${city}`),
        ]);
        if (locResp.data && Array.isArray(locResp.data.localities)) {
          const validLocs = locResp.data.localities
            .map((l) => getItemName(l, "locality"))
            .filter((l) => typeof l === "string" && l.trim() !== "");
          if (validLocs.length > 0) setLocalitiesList(validLocs);
        }
        if (aptResp.data && Array.isArray(aptResp.data.apartments)) {
          const validApts = aptResp.data.apartments
            .map((a) => {
              if (typeof a === "string") return { name: a, locality: "" };
              return {
                name: a.apartment || a.name || "",
                locality: a.locality || "",
              };
            })
            .filter((a) => a.name && a.name.trim() !== "");
          if (validApts.length > 0) setApartmentsList(validApts);
        }
      } catch (e) {
        // keep defaults
      }
    };
    fetchCityData();
  }, [city]);

  // Scoring update
  useEffect(() => {
    let score = 0;
    if (lookingTo && listingType && propertyCategory) score += 12;
    if (city && locality) score += 15;
    if (bedrooms && bathrooms) score += 12;
    dispatch(setbasicdetailScooring(score > 0 ? 12 : 0));
    dispatch(setLocalityScooring(locality ? 15 : 0));
    dispatch(setmainpropertyScooring(bedrooms ? 12 : 0));
    dispatch(setpropertyScooring());
  }, [lookingTo, listingType, propertyCategory, city, locality, bedrooms, bathrooms]);

  const validateForm = () => {
    const newErrors = {};
    if (!city || city.trim() === "") newErrors.city = "City is required";
    if (!locality || locality.trim() === "") newErrors.locality = "Locality is required";
    if (!bedrooms) newErrors.bedrooms = "Select number of bedrooms";
    if (!bathrooms) newErrors.bathrooms = "Select number of bathrooms";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateForm()) {
      window.scrollTo({ top: 150, behavior: "smooth" });
      return;
    }

    const basicData = {
      supercatName: lookingTo,
      catName: listingType,
      subcatName: propertyCategory,
    };

    const locationData = {
      city: city,
      apartment: apartment,
      locality: locality,
      subLocality: subLocality,
      housenumber: houseNumber,
    };

    const profileData = {
      noofBedrooms: bedrooms,
      noofBathrooms: bathrooms,
      balconies: balconies,
      poojaroom: otherRooms.poojaRoom,
      studyroom: otherRooms.studyRoom,
      servantroom: otherRooms.servantRoom,
      storeroom: otherRooms.storeRoom,
      furnishedStatus: furnishing,
      availabilityStatus: availabilityStatus,
      carpetArea: areas.carpetArea || "1625",
      builtupArea: areas.builtupArea,
      superBuiltupArea: areas.superBuiltupArea,
      plotArea: areas.plotArea,
      coveredParking: coveredParking,
      openParking: openParking,
      floorDetails: floorDetails,
    };

    dispatch(setBasicDetails(basicData));
    dispatch(setLocationDetails(locationData));
    dispatch(setPropertyProfile(profileData));

    // Move to Photos step (step 3 in overall stepper)
    dispatch(setPostPropertyStepsStatus(3));
    if (onNext) onNext();
  };

  const handleReset = () => {
    setCity("Hyderabad");
    setApartment("");
    setLocality("");
    setSubLocality("");
    setHouseNumber("");
    setBedrooms("3");
    setBathrooms("3");
    setBalconies("1");
    setOtherRooms({
      poojaRoom: false,
      studyRoom: false,
      servantRoom: false,
      storeRoom: false,
    });
    setFurnishing("Un-furnished");
    setAvailabilityStatus("Ready to move");
    setAreas({
      carpetArea: "1625",
      builtupArea: "",
      superBuiltupArea: "",
      plotArea: "",
      landArea: "",
    });
    setCoveredParking(1);
    setOpenParking(0);
    setErrors({});
  };

  return (
    <div className="mgh-step-one">
      {/* Banner Header */}
      <div className="mgh-post-banner-header">
        <div className="mgh-post-banner-icon">
          <FiFileText />
        </div>
        <div>
          <h2 className="mgh-post-banner-title">
            Post Your <span>Property</span>
          </h2>
          <p className="mgh-post-banner-sub">
            List your property in just a few steps and connect with genuine buyers.
          </p>
        </div>
      </div>

      {/* CARD 1: Basic Details */}
      <div className="mgh-post-card">
        <div className="mgh-section-header">
          <span className="mgh-section-num">1</span>
          <div className="mgh-section-title-wrap">
            <h3>Basic Details</h3>
            <p>Tell us what kind of property you want to list</p>
          </div>
        </div>

        <div className="mgh-form-row mgh-form-row-3">
          {/* I'm looking to */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              I'm looking to <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <FiHome className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={lookingTo}
                onChange={(e) => setLookingTo(e.target.value)}
              >
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>
          </div>

          {/* Listing Type */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              Listing Type <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <FiTag className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={listingType}
                onChange={(e) => setListingType(e.target.value)}
              >
                <option value="Sell">Sell</option>
                <option value="Rent">Rent</option>
                <option value="PG">PG</option>
                <option value="Lease">Lease</option>
              </select>
            </div>
          </div>

          {/* Property Category */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              Property Category <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <BiBuildingHouse className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={propertyCategory}
                onChange={(e) => setPropertyCategory(e.target.value)}
              >
                <option value="Flat/Apartment">Flat/Apartment</option>
                <option value="Independent House/Villa">Independent House/Villa</option>
                <option value="Independent Floor">Independent Floor</option>
                <option value="Residential Plot">Residential Plot</option>
                <option value="Penthouse">Penthouse</option>
                <option value="Studio Apartment">Studio Apartment</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* CARD 2: Property Location */}
      <div className="mgh-post-card">
        <div className="mgh-section-header">
          <span className="mgh-section-num">2</span>
          <div className="mgh-section-title-wrap">
            <h3>Property Location</h3>
            <p>An accurate location helps you connect with the right buyers</p>
          </div>
        </div>

        <div className="mgh-form-row mgh-form-row-2">
          {/* City */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              City <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <FiMapPin className="mgh-ppt-icon" />
              <input
                type="text"
                className="mgh-ppt-input"
                placeholder="City"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setShowCityDropdown(true);
                  if (errors.city) setErrors((prev) => ({ ...prev, city: null }));
                }}
                onFocus={() => setShowCityDropdown(true)}
              />
            </div>
            {errors.city && (
              <span style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                {errors.city}
              </span>
            )}
            {showCityDropdown && citiesList.length > 0 && (
              <div className="mgh-autocomplete-dropdown">
                {citiesList
                  .filter((c) =>
                    getItemName(c, "city")
                      .toLowerCase()
                      .includes((city || "").toLowerCase())
                  )
                  .slice(0, 8)
                  .map((item, idx) => {
                    const cityName = getItemName(item, "city");
                    return (
                      <div
                        key={idx}
                        className="mgh-autocomplete-item"
                        onClick={() => {
                          setCity(cityName);
                          setShowCityDropdown(false);
                        }}
                      >
                        {cityName}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Apartment / Society */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              Apartment / Society (Optional)
            </label>
            <div className="mgh-ppt-input-wrap">
              <BiBuildingHouse className="mgh-ppt-icon" />
              <input
                type="text"
                className="mgh-ppt-input"
                placeholder="Enter apartment or society name"
                value={apartment}
                onChange={(e) => {
                  setApartment(e.target.value);
                  setShowApartmentDropdown(true);
                }}
                onFocus={() => setShowApartmentDropdown(true)}
              />
            </div>
            {showApartmentDropdown && apartmentsList.length > 0 && (
              <div className="mgh-autocomplete-dropdown">
                {apartmentsList
                  .filter((a) => {
                    const aptName = typeof a === "string" ? a : a.name || a.apartment || "";
                    return aptName
                      .toLowerCase()
                      .includes((apartment || "").toLowerCase());
                  })
                  .slice(0, 6)
                  .map((item, idx) => {
                    const aptName = typeof item === "string" ? item : item.name || item.apartment || "";
                    const aptLoc = typeof item === "object" ? item.locality : "";
                    return (
                      <div
                        key={idx}
                        className="mgh-autocomplete-item"
                        onClick={() => {
                          setApartment(aptName);
                          if (aptLoc && !locality) setLocality(aptLoc);
                          setShowApartmentDropdown(false);
                        }}
                      >
                        {aptName}
                        {aptLoc && (
                          <span style={{ color: "#94a3b8", fontSize: "12px", marginLeft: "6px" }}>
                            ({aptLoc})
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        <div className="mgh-form-row mgh-form-row-2">
          {/* Locality */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              Locality <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <FiMapPin className="mgh-ppt-icon" />
              <input
                type="text"
                className="mgh-ppt-input"
                placeholder="Enter locality"
                value={locality}
                onChange={(e) => {
                  setLocality(e.target.value);
                  setShowLocalityDropdown(true);
                  if (errors.locality) setErrors((prev) => ({ ...prev, locality: null }));
                }}
                onFocus={() => setShowLocalityDropdown(true)}
              />
            </div>
            {errors.locality && (
              <span style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                {errors.locality}
              </span>
            )}
            {showLocalityDropdown && localitiesList.length > 0 && (
              <div className="mgh-autocomplete-dropdown">
                {localitiesList
                  .filter((l) =>
                    getItemName(l, "locality")
                      .toLowerCase()
                      .includes((locality || "").toLowerCase())
                  )
                  .slice(0, 8)
                  .map((item, idx) => {
                    const locName = getItemName(item, "locality");
                    return (
                      <div
                        key={idx}
                        className="mgh-autocomplete-item"
                        onClick={() => {
                          setLocality(locName);
                          setShowLocalityDropdown(false);
                        }}
                      >
                        {locName}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Sub-Locality */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">Sub-Locality (Optional)</label>
            <div className="mgh-ppt-input-wrap">
              <BiBuildingHouse className="mgh-ppt-icon" />
              <input
                type="text"
                className="mgh-ppt-input"
                placeholder="Enter sub-locality"
                value={subLocality}
                onChange={(e) => setSubLocality(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* House No */}
        <div className="mgh-form-row">
          <div className="mgh-form-group">
            <label className="mgh-form-label">House No. (Optional)</label>
            <div className="mgh-ppt-input-wrap">
              <FiHome className="mgh-ppt-icon" />
              <input
                type="text"
                className="mgh-ppt-input"
                placeholder="Enter house number"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CARD 3: Property Profile */}
      <div className="mgh-post-card">
        <div className="mgh-section-header">
          <span className="mgh-section-num">3</span>
          <div className="mgh-section-title-wrap">
            <h3>Property Profile</h3>
            <p>Share the key details about your property</p>
          </div>
        </div>

        <div className="mgh-form-row mgh-form-row-3">
          {/* Bedrooms */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              No. of Bedrooms <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <BiBed className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={bedrooms}
                onChange={(e) => setBedrooms(e.target.value)}
              >
                <option value="1">1 BHK</option>
                <option value="2">2 BHK</option>
                <option value="3">3 BHK</option>
                <option value="4">4 BHK</option>
                <option value="5">5+ BHK</option>
              </select>
            </div>
          </div>

          {/* Bathrooms */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">
              No. of Bathrooms <span className="required">*</span>
            </label>
            <div className="mgh-ppt-input-wrap">
              <BiBath className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={bathrooms}
                onChange={(e) => setBathrooms(e.target.value)}
              >
                <option value="1">1 Bathroom</option>
                <option value="2">2 Bathrooms</option>
                <option value="3">3 Bathrooms</option>
                <option value="4">4 Bathrooms</option>
                <option value="5">5+ Bathrooms</option>
              </select>
            </div>
          </div>

          {/* Balconies */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">No. of Balconies</label>
            <div className="mgh-ppt-input-wrap">
              <MdOutlineBalcony className="mgh-ppt-icon" />
              <select
                className="mgh-ppt-select"
                value={balconies}
                onChange={(e) => setBalconies(e.target.value)}
              >
                <option value="0">0 Balconies</option>
                <option value="1">1 Balcony</option>
                <option value="2">2 Balconies</option>
                <option value="3">3 Balconies</option>
                <option value="4">4+ Balconies</option>
              </select>
            </div>
          </div>
        </div>

        {/* Other Rooms (Optional) */}
        <div style={{ marginBottom: "20px" }}>
          <label className="mgh-form-label">Other rooms (Optional)</label>
          <div className="mgh-pills-wrap">
            {[
              { key: "poojaRoom", label: "Pooja Room" },
              { key: "studyRoom", label: "Study Room" },
              { key: "servantRoom", label: "Servant Room" },
              { key: "storeRoom", label: "Store Room" },
            ].map((room) => {
              const active = otherRooms[room.key];
              return (
                <button
                  type="button"
                  key={room.key}
                  className={`mgh-pill-btn ${active ? "active" : ""}`}
                  onClick={() =>
                    setOtherRooms((prev) => ({ ...prev, [room.key]: !prev[room.key] }))
                  }
                >
                  {active ? <FiCheck /> : <FiPlus />}
                  {room.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Furnishing & Availability Status */}
        <div className="mgh-form-row mgh-form-row-2">
          {/* Furnishing */}
          <div>
            <label className="mgh-form-label">Furnishing</label>
            <div className="mgh-pills-wrap">
              {["Furnished", "Semi-furnished", "Un-furnished"].map((f) => (
                <button
                  type="button"
                  key={f}
                  className={`mgh-pill-btn ${furnishing === f ? "active" : ""}`}
                  onClick={() => setFurnishing(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Availability Status */}
          <div>
            <label className="mgh-form-label">Availability Status</label>
            <div className="mgh-pills-wrap">
              {["Ready to move", "Under construction"].map((status) => (
                <button
                  type="button"
                  key={status}
                  className={`mgh-pill-btn ${
                    availabilityStatus === status ? "active" : ""
                  }`}
                  onClick={() => setAvailabilityStatus(status)}
                >
                  <span className="radio-dot"></span>
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CARD 4: Area Details & Parking */}
      <div className="mgh-post-card">
        <div className="mgh-section-header">
          <span className="mgh-section-num">4</span>
          <div className="mgh-section-title-wrap">
            <h3>Area Details</h3>
            <p>Area details are optional but help buyers calculate pricing</p>
          </div>
        </div>

        {/* Area Pills */}
        <div style={{ marginBottom: "20px" }}>
          <div className="mgh-pills-wrap">
            {[
              { key: "carpetArea", label: "Carpet Area" },
              { key: "builtupArea", label: "Built-up Area" },
              { key: "superBuiltupArea", label: "Super Built-up Area" },
              { key: "plotArea", label: "Plot Area" },
              { key: "landArea", label: "Land Area" },
            ].map((item) => (
              <button
                type="button"
                key={item.key}
                className={`mgh-pill-btn ${showAreaInput[item.key] ? "active" : ""}`}
                onClick={() =>
                  setShowAreaInput((prev) => ({
                    ...prev,
                    [item.key]: !prev[item.key],
                  }))
                }
              >
                {showAreaInput[item.key] ? <FiCheck /> : <FiPlus />}
                {showAreaInput[item.key] ? item.label : `Add ${item.label}`}
              </button>
            ))}
          </div>

          {/* Area Inputs display */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "14px",
              marginTop: "16px",
            }}
          >
            {showAreaInput.carpetArea && (
              <div className="mgh-form-group">
                <label className="mgh-form-label">Carpet Area (sq.ft.)</label>
                <div className="mgh-ppt-input-wrap">
                  <input
                    type="number"
                    className="mgh-ppt-input"
                    placeholder="e.g. 1625"
                    value={areas.carpetArea}
                    onChange={(e) =>
                      setAreas((prev) => ({ ...prev, carpetArea: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}
            {showAreaInput.builtupArea && (
              <div className="mgh-form-group">
                <label className="mgh-form-label">Built-up Area (sq.ft.)</label>
                <div className="mgh-ppt-input-wrap">
                  <input
                    type="number"
                    className="mgh-ppt-input"
                    placeholder="e.g. 1800"
                    value={areas.builtupArea}
                    onChange={(e) =>
                      setAreas((prev) => ({ ...prev, builtupArea: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}
            {showAreaInput.superBuiltupArea && (
              <div className="mgh-form-group">
                <label className="mgh-form-label">Super Built-up Area (sq.ft.)</label>
                <div className="mgh-ppt-input-wrap">
                  <input
                    type="number"
                    className="mgh-ppt-input"
                    placeholder="e.g. 2100"
                    value={areas.superBuiltupArea}
                    onChange={(e) =>
                      setAreas((prev) => ({
                        ...prev,
                        superBuiltupArea: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}
            {showAreaInput.plotArea && (
              <div className="mgh-form-group">
                <label className="mgh-form-label">Plot Area (sq.yards)</label>
                <div className="mgh-ppt-input-wrap">
                  <input
                    type="number"
                    className="mgh-ppt-input"
                    placeholder="e.g. 250"
                    value={areas.plotArea}
                    onChange={(e) =>
                      setAreas((prev) => ({ ...prev, plotArea: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reserved Parking & Floor Details */}
        <div className="mgh-form-row mgh-form-row-2" style={{ alignItems: "flex-end" }}>
          {/* Reserved Parking */}
          <div>
            <label className="mgh-form-label">Reserved Parking (Optional)</label>
            <div style={{ display: "flex", gap: "18px", flexWrap: "wrap", marginTop: "4px" }}>
              <div className="mgh-counter-wrap">
                <span style={{ fontSize: "13px", color: "#64748b" }}>Covered:</span>
                <div className="mgh-counter-box">
                  <button
                    type="button"
                    className="mgh-counter-btn"
                    onClick={() => setCoveredParking((p) => Math.max(0, p - 1))}
                  >
                    <FiMinus />
                  </button>
                  <span className="mgh-counter-value">{coveredParking}</span>
                  <button
                    type="button"
                    className="mgh-counter-btn"
                    onClick={() => setCoveredParking((p) => p + 1)}
                  >
                    <FiPlus />
                  </button>
                </div>
              </div>

              <div className="mgh-counter-wrap">
                <span style={{ fontSize: "13px", color: "#64748b" }}>Open:</span>
                <div className="mgh-counter-box">
                  <button
                    type="button"
                    className="mgh-counter-btn"
                    onClick={() => setOpenParking((p) => Math.max(0, p - 1))}
                  >
                    <FiMinus />
                  </button>
                  <span className="mgh-counter-value">{openParking}</span>
                  <button
                    type="button"
                    className="mgh-counter-btn"
                    onClick={() => setOpenParking((p) => p + 1)}
                  >
                    <FiPlus />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Floor Details */}
          <div className="mgh-form-group">
            <label className="mgh-form-label">Floor Details</label>
            <div className="mgh-ppt-input-wrap">
              <select
                className="mgh-ppt-select"
                value={floorDetails}
                onChange={(e) => setFloorDetails(e.target.value)}
              >
                <option value="Ground Floor">Ground Floor</option>
                <option value="First Floor">First Floor</option>
                <option value="Second Floor">Second Floor</option>
                <option value="Third Floor">Third Floor</option>
                <option value="Fourth Floor">Fourth Floor</option>
                <option value="Fifth Floor or above">Fifth Floor or above</option>
              </select>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="mgh-form-actions">
          <button
            type="button"
            className="mgh-btn-secondary"
            onClick={handleReset}
          >
            Reset All
          </button>
          <button
            type="button"
            className="mgh-btn-primary"
            onClick={handleNext}
          >
            Next: Add Photos →
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepOneDetails;
